import {
  APP_TROVE_EVENTS,
  firebaseEventMapping,
  singularEventMapping,
  getAppSourceDetails,
} from "./webApputils/analyticEvents";

interface EventPayload {
  [key: string]: any;
}

export enum AnalyticsPlatform {
  AMPLITUDE = "amplitude",
  SINGULAR = "singular",
  META = "meta",
  FIREBASE = "firebase",
  APP_TROVE = "app_trove",
  ALL = "all",
}
const analyticsPlatformsArray: string[] = Object.values(AnalyticsPlatform);

interface LogEventOptions {
  platforms?: AnalyticsPlatform[];
  eventName: string;
  payload?: EventPayload;
}

/**
 * Common function to log events to one or all analytics platforms
 * @param options - Options for logging events
 * @param options.platforms - Array of platforms to log to ('amplitude' | 'singular' | 'all'). Defaults to 'all'
 * @param options.eventName - Name of the event to log
 * @param options.payload - Additional event properties
 */
export const logAppEvent = ({
  platforms = [AnalyticsPlatform.ALL],
  eventName,
  payload,
}: LogEventOptions) => {
  if (!__CLIENT__ || !window.ReactNativeWebView) return;

  const shouldLogToAll = platforms.includes(AnalyticsPlatform.ALL);
  const eventsToLog = shouldLogToAll
    ? analyticsPlatformsArray.filter((platform) => platform !== AnalyticsPlatform.ALL)
    : platforms;

  const payloadWithSourceDetails = { ...payload, ...getAppSourceDetails() };

  eventsToLog.forEach((platform) => {
    let updatedEventName = eventName;
    if (platform === AnalyticsPlatform.FIREBASE) {
      updatedEventName = firebaseEventMapping[eventName] || eventName;
    }
    if (platform === AnalyticsPlatform.SINGULAR) {
      updatedEventName = singularEventMapping[eventName] || eventName;
    }
    if (platform === AnalyticsPlatform.APP_TROVE) {
      updatedEventName = APP_TROVE_EVENTS[eventName] || eventName;
    }
    const message = {
      type: `${platform.toUpperCase()}_EVENT`,
      eventName: updatedEventName,
      payload: payloadWithSourceDetails || {},
    };

    if (window.ReactNativeWebView?.postMessage) {
      window.ReactNativeWebView.postMessage(JSON.stringify(message));
    }
  });
};

/**
 * Sends an event to Amplitude via React Native bridge
 * @param eventName - Name of the event to log
 * @param payload - Additional event properties
 */
export const logAmplitudeEvent = (eventName: string, payload?: EventPayload) => {
  logAppEvent({ platforms: [AnalyticsPlatform.AMPLITUDE], eventName, payload });
};

/**
 * Sends an event to Singular via React Native bridge
 * @param eventName - Name of the event to log
 * @param payload - Additional event properties
 */
export const logSingularEvent = (eventName: string, payload?: EventPayload) => {
  logAppEvent({ platforms: [AnalyticsPlatform.SINGULAR], eventName, payload });
};

/**
 * Sends an event to Meta via React Native bridge
 * @param eventName - Name of the event to log
 * @param payload - Additional event properties
 */
export const logMetaEvent = (eventName: string, payload?: EventPayload) => {
  logAppEvent({ platforms: [AnalyticsPlatform.META], eventName, payload });
};
// Add type definition for ReactNativeWebView
declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}
